import Vue from 'vue';
import ActiveFilters from './components/Subscriptions/ActiveFilters.vue';
import AdvertisementCard from './components/Subscriptions/AdvertisementCard.vue';
import FilterPill from './components/Subscriptions/FilterPill.vue';
import SubscriptionFooter from './components/Subscriptions/Footer.vue';
import MultiSelectDropdown from './components/Subscriptions/MultiSelectDropdown.vue';
import NewsletterCard from './components/Subscriptions/NewsletterCard.vue';
import NewsletterGroup from './components/Subscriptions/NewsletterGroup.vue';
import Notification from './components/Subscriptions/Notification.vue';

import 'es6-promise/auto';

import VueLoadScript from 'vue-load-script-plus'
Vue.use(VueLoadScript);

if (document.getElementById('app')) {
    const app = new Vue(
        {
            el: '#app',
            components: {
                'active-filters': ActiveFilters,
                'advertisement-card': AdvertisementCard,
                'filter-pill': FilterPill,
                'multi-select-dropdown': MultiSelectDropdown,
                'newsletter-card': NewsletterCard,
                'newsletter-group': NewsletterGroup,
                'notification': Notification,
                'subscription-footer': SubscriptionFooter,
            },
            data() {
                return {
                    allNewsletters: {},
                    currentSubscriptions: [],
                    initialSubscriptions: [],
                    marketOptions: [],
                    notification: {
                        show: false,
                        type: 'success',
                        title: '',
                        subtitle: '',
                    },
                    selectedMarkets: [],
                    selectedTopics: [],
                    topicOptions: [],
                };
            },
            computed: {
                subscriptionChanges() {
                    const changes = [];

                    this.currentSubscriptions.forEach((id) => {
                        if (! this.initialSubscriptions.includes(id)) {
                            changes.push({ id, action: 'subscribe' });
                        }
                    });

                    this.initialSubscriptions.forEach((id) => {
                        if (! this.currentSubscriptions.includes(id)) {
                            changes.push({ id, action: 'unsubscribe' });
                        }
                    });

                    return changes;
                },
                hasChanges() {
                    return this.subscriptionChanges.length > 0;
                },
                allSelectedFilters() {
                    return [...this.selectedMarkets, ...this.selectedTopics];
                },
                hasAnyFilters() {
                    return this.selectedMarkets.length > 0 || this.selectedTopics.length > 0;
                },
            },
            watch: {
                allSelectedFilters: {
                    handler() {
                        this.updateHash();
                        this.updateGroupExpansion();
                    },
                    deep: true,
                },
            },
            methods: {
                handleSubscriptionChange(event) {
                    const { newsletter, selected, isInitialState } = event;

                    this.$set(this.allNewsletters, newsletter.id, newsletter);

                    if (isInitialState) {
                        if (selected && ! this.currentSubscriptions.includes(newsletter.id)) {
                            this.currentSubscriptions.push(newsletter.id);

                            // Only add to initialSubscriptions if they were actually subscribed (not just preselected)
                            if (newsletter.subscribed && ! this.initialSubscriptions.includes(newsletter.id)) {
                                this.initialSubscriptions.push(newsletter.id);
                            }
                        }
                    } else if (selected) {
                        if (! this.currentSubscriptions.includes(newsletter.id)) {
                            this.currentSubscriptions.push(newsletter.id);
                        }
                    } else {
                        const index = this.currentSubscriptions.indexOf(newsletter.id);
                        if (index > -1) {
                            this.currentSubscriptions.splice(index, 1);
                        }
                    }
                },
                handleSubscribe(data) {
                    this.showNotification('success', 'Your preferences are updated.');
                    this.initialSubscriptions = [...this.currentSubscriptions];
                },
                handleSubscribeError(error) {
                    console.error('Subscription error:', error);
                    this.showNotification('error', error.title || 'Update failed.', error.message || 'Please try again or contact support.');
                },
                handleMarketChange(marketId) {
                    if (this.selectedMarkets.includes(marketId)) {
                        this.selectedMarkets = this.selectedMarkets.filter((id) => id !== marketId);
                    } else {
                        this.selectedMarkets.push(marketId);
                    }
                },
                handleTopicChange(topicId) {
                    if (this.selectedTopics.includes(topicId)) {
                        this.selectedTopics = this.selectedTopics.filter((id) => id !== topicId);
                    } else {
                        this.selectedTopics.push(topicId);
                    }
                },
                handleFilterRemove(filter) {
                    if (filter.type === 'market') {
                        this.selectedMarkets = this.selectedMarkets.filter((id) => id !== filter.value);
                    } else if (filter.type === 'topic') {
                        this.selectedTopics = this.selectedTopics.filter((id) => id !== filter.value);
                    }
                },
                removeNotification() {
                    this.notification.show = false;
                },
                resetAllFilters() {
                    this.selectedMarkets = [];
                    this.selectedTopics = [];
                },
                updateHash() {
                    const ids = [...this.selectedMarkets, ...this.selectedTopics];
                    if (ids.length > 0) {
                        window.location.hash = `classifications=${ids.join(',')}`;
                    } else {
                        window.location.hash = '';
                    }
                },
                parseHash() {
                    const hash = window.location.hash;
                    if (hash.startsWith('#classifications=')) {
                        const ids = hash.replace('#classifications=', '')
                            .split(',')
                            .filter(Boolean);

                        ids.forEach((id) => {
                            id = parseInt(id);
                            const market = this.marketOptions.find((m) => m.id === id || m.value === id);
                            const topic = this.topicOptions.find((t) => t.id === id || t.value === id);

                            if (market && ! this.selectedMarkets.includes(id)) {
                                this.selectedMarkets.push(id);
                            } else if (topic && ! this.selectedTopics.includes(id)) {
                                this.selectedTopics.push(id);
                            }
                        });
                    }
                },
                initializeData() {
                    const appElement = document.getElementById('app');
                    if (appElement) {
                        try {
                            const rawMarkets = JSON.parse(appElement.dataset.markets || '{}');
                            const rawTopics = JSON.parse(appElement.dataset.topics || '{}');

                            this.marketOptions = Object.entries(rawMarkets).map(([id, name]) => ({
                                id: parseInt(id),
                                value: parseInt(id),
                                name: name,
                                label: name,
                            })).sort(function (a, b) {
                                return a.name.localeCompare(b.name);
                            });

                            this.topicOptions = Object.entries(rawTopics).map(([id, name]) => ({
                                id: parseInt(id),
                                value: parseInt(id),
                                name: name,
                                label: name,
                            })).sort(function (a, b) {
                                return a.name.localeCompare(b.name);
                            });
                        } catch (e) {
                            console.warn('Failed to parse markets/topics data:', e);
                            this.marketOptions = [];
                            this.topicOptions = [];
                        }
                    }
                },
                updateGroupExpansion() {
                    this.$nextTick(() => {
                        let firstVisibleGroupExpanded = false;

                        // Iterate through all newsletter groups
                        Object.keys(this.$refs).forEach((refKey) => {
                            if (refKey.startsWith('newsletterGroup')) {
                                const groupComponents = Array.isArray(this.$refs[refKey])
                                    ? this.$refs[refKey]
                                    : [this.$refs[refKey]];

                                groupComponents.forEach((groupComponent) => {
                                    if (groupComponent && groupComponent.shouldShowGroup) {
                                        if (firstVisibleGroupExpanded) {
                                            groupComponent.resetExpanded();
                                        } else if (groupComponent.group.dynamic) {
                                            groupComponent.resetExpanded();
                                        } else {
                                            groupComponent.setExpanded(true);
                                        }
                                        firstVisibleGroupExpanded = true;
                                    }
                                });
                            }
                        });
                    });
                },
                hideNotification() {
                    this.notification.show = false;
                },
                showNotification(type, title, subtitle = '') {
                    this.notification = {
                        show: true,
                        type: type,
                        title: title,
                        subtitle: subtitle,
                    };

                    this.$nextTick(() => {
                        const notificationEl = this.$refs.notification && this.$refs.notification.$el;
                        if (notificationEl) {
                            notificationEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    });
                },
            },
            mounted() {
                this.initializeData();
                this.parseHash();

                // Hide static content and show Vue components
                document.getElementById('app').classList.add('vue-ready');
                this.$nextTick(() => {
                    document.querySelectorAll('.vue-content').forEach((el) => {
                        el.style.display = 'block';
                    });
                });
            },
        }
    )
}
